import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// Shared default request timeout (ms) so axiosInstance and the raw axios
// singleton below can never drift apart.
const DEFAULT_REQUEST_TIMEOUT_MS = 30000;
// NOTE: this timeout bounds total request+response duration, not inactivity - it can't
// distinguish a hung server from a slow-but-still-streaming large response body. Currently
// safe: no frontend call fetches an unbounded/large/binary payload (see known-issues.md,
// task-058 fix_review, 2026-09-12). Re-evaluate if a bulk export/report/unbounded listing
// endpoint is ever added.

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
  timeout: DEFAULT_REQUEST_TIMEOUT_MS,
});

// Mutate the global default axios instance too, so orders.ts/cart.ts/auth.ts
// (which deliberately call the raw `axios` export directly instead of
// axiosInstance) and this file's own token-refresh axios.post call below are
// also covered by the same timeout. This runs at module-load time, before
// any user-triggered handler can fire, so there is no real race with the
// modules that read the raw axios export.
axios.defaults.timeout = DEFAULT_REQUEST_TIMEOUT_MS;

axiosInstance.interceptors.request.use(
  (config) => {
    // Check for admin token first (for admin routes), then regular token
    const adminToken = localStorage.getItem('adminToken');
    const userToken = localStorage.getItem('access_token');

    // Use admin token for admin routes, otherwise use regular token
    const token = config.url?.includes('/admin') ? adminToken : userToken || adminToken;

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Check if this is an admin request
      const isAdminRequest = originalRequest.url?.includes('/admin');
      const refreshToken = localStorage.getItem('refresh_token');

      if (refreshToken && !isAdminRequest) {
        try {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refresh_token: refreshToken,
          });

          const { access_token } = response.data;
          localStorage.setItem('access_token', access_token);

          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return axiosInstance(originalRequest);
        } catch (refreshError) {
          // A timed-out refresh call (slow-but-alive network) is not the same as a
          // genuinely invalid/expired refresh token -- don't destroy the session.
          // Let the original request that triggered this refresh simply fail with
          // its own error (the final `return Promise.reject(error)` below); the
          // user stays logged in and can retry.
          if ((refreshError as { code?: string })?.code !== 'ECONNABORTED') {
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            localStorage.removeItem('user');
            window.location.href = '/login';
          }
        }
      } else if (isAdminRequest) {
        // For admin requests, clear admin tokens and redirect to admin login
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');

        // Only redirect if we're not already on the admin login page
        if (!window.location.pathname.includes('/admin/login')) {
          window.location.href = '/admin/login';
        }
      } else {
        // No refresh token for regular user, redirect to login
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;